import { QuestionCircleOutlined } from '@ant-design/icons'
import { Space, Tooltip } from 'antd'
import dayjs, { Dayjs } from 'dayjs'

import { mapNumberEnumOptions } from '@eastcoal/common/utils/enums'

import { DateRangePicker } from '@eastcoal/formily/components/DatePicker'
import { FormGrid } from '@eastcoal/formily/components/FormGrid'
import { FormItem } from '@eastcoal/formily/components/FormItem'
import { Input } from '@eastcoal/formily/components/Input'
import { InputNumber } from '@eastcoal/formily/components/InputNumber'
import { SchemaItem } from '@eastcoal/formily/components/SchemaForm/interface'
import { Select } from '@eastcoal/formily/components/Select'
import { Switch } from '@eastcoal/formily/components/Switch'

import { AnnouncementSelect } from '@eastcoal/shared/@formily/AnnouncementSelect'
import { AreaCascader } from '@eastcoal/shared/@formily/AreaCascader'
import { ContractTemplateSelect } from '@eastcoal/shared/@formily/ContractTemplateSelect'
import { DictSelect } from '@eastcoal/shared/@formily/DictSelect'
import { KindAllNodeCascader } from '@eastcoal/shared/@formily/KindAllNodeCascader'
import { MemberSelect } from '@eastcoal/shared/components/MemberSelect'
import { PricingMethodSelect } from '@eastcoal/shared/components/PricingMethodSelect'
import { AnnouncementType } from '@eastcoal/shared/enums/AnnouncementType'
import { AuctionCode } from '@eastcoal/shared/enums/AuctionCode'
import { BizType } from '@eastcoal/shared/enums/BizType'
import { DealPriceTypeTextMap } from '@eastcoal/shared/enums/DealPriceType'
import { MemberType } from '@eastcoal/shared/enums/MemberType'
import { SplitMethodTextMap } from '@eastcoal/shared/enums/SplitMethod'

import { CertificationInput } from '../../../components/Form/CertificationInput'
import { DepositInput } from '../../../components/Form/DepositInput'
import { EndTimePicker } from '../../../components/Form/EndTimePicker'
import { EntranceFeeInput } from '../../../components/Form/EntranceFeeInput'
import { InputNumberRange } from '../../../components/Form/InputNumberRange'
import { ServiceFeeInput } from '../../../components/Form/ServiceFeeInput'
import { SplitCountInput } from '../../../components/Form/SplitCountInput'
import { SplitValuesInput } from '../../../components/Form/SplitValuesInput'
import { StartPriceInput } from '../../../components/Form/StartPriceInput'
import { StartTimePicker } from '../../../components/Form/StartTimePicker'
import { TimeMethodInput } from '../../../components/Form/TimeMethodInput'
import { MAX_DEAL_COUNT } from '../../constants/config'
import {
  KIND_FIELD,
  MEMBER_FIELD,
  ANNOUNCEMENT_FIELD,
  PRICING_METHOD_FIELD,
  SPLIT_COUNT_FIELD,
  SERVICE_FEE_FIELD,
  START_PRICE_FIELD,
  LIMIT_IP_FIELD,
  STORAGE_PLACE_FIELD
} from '../../constants/field'
import { addPriceRangeValidator } from '../validators/addPriceRange'
import { delayCountValidator } from '../validators/delayCount'
import { depositValidator } from '../validators/deposit'
import { endTimeValidator } from '../validators/endTime'
import { entranceFeeValidator } from '../validators/entranceFee'
import { quantityValidator } from '../validators/quantity'
import { serviceFeeValidator } from '../validators/serviceFee'
import { splitCountValidator } from '../validators/splitCount'
import { splitValuesValidator } from '../validators/splitValue'
import { startPriceValidator } from '../validators/startPrice'
import { startTimeValidator } from '../validators/startTime'

export const basicSchema: Record<string, SchemaItem> = {
  basicBlock: {
    type: 'void',
    'x-component': FormGrid,
    'x-component-props': {
      columns: 2
    },
    properties: {
      [MEMBER_FIELD]: {
        title: '发布公司',
        type: 'object',
        'x-pattern': 'disabled',
        'x-decorator': FormItem,
        'x-component': MemberSelect,
        'x-component-props': {
          type: MemberType.ENTERPRISE
        }
      },
      kindFormGrid: {
        type: 'void',
        'x-component': FormGrid,
        'x-component-props': {
          wrap: false,
          columns: 2
        },
        properties: {
          [KIND_FIELD]: {
            title: '品种',
            type: 'object',
            required: true,
            'x-decorator': FormItem,
            'x-component': KindAllNodeCascader
          },
          productName: {
            title: '标的物',
            type: 'string',
            required: true,
            'x-pattern': 'disabled',
            'x-decorator': FormItem,
            'x-component': Input,
            'x-component-props': {
              placeholder: '{{!$values.kind?.id ? "请先选择品种":""}}'
            }
          }
        }
      },
      [ANNOUNCEMENT_FIELD]: {
        title: '关联预告',
        type: 'object',
        required: true,
        'x-pattern': 'disabled',
        'x-decorator': FormItem,
        'x-component': AnnouncementSelect,
        'x-component-props': {
          readyRequest: false,
          type: AnnouncementType.APPLY,
          kindId: '{{$values.kindId}}'
        },
        'x-validator': [
          {
            required: true,
            message: '请选择竞价预告'
          }
        ]
      },
      title: {
        title: '场次标题',
        type: 'string',
        'x-pattern': 'disabled',
        'x-decorator': FormItem,
        'x-component': Input,
        'x-component-props': {
          placeholder: '根据发布公司和品种自动生成'
        }
      },
      factory: {
        title: '生产单位',
        type: 'string',
        'x-decorator': FormItem,
        'x-component': Input
      },
      [`{requireCertification,certificationName}`]: {
        type: 'object',
        title: '额外资质要求',
        'x-decorator': FormItem,
        'x-decorator-props': {
          fullness: true
        },
        'x-component': CertificationInput
      },
      [STORAGE_PLACE_FIELD]: {
        type: 'object',
        title: '存放地',
        required: true,
        'x-decorator': FormItem,
        'x-component': AreaCascader
      },
      [`{
        id: deliveryPlaceId,
        code: deliveryPlaceCode,
        name: deliveryPlaceName
      }`]: {
        type: 'object',
        title: '交货地',
        'x-decorator': FormItem,
        'x-component': AreaCascader
      },
      paymentMethod: {
        type: 'string',
        title: '付款方式',
        required: true,
        'x-decorator': FormItem,
        'x-component': DictSelect,
        'x-component-props': {
          code: AuctionCode.PAYMENT_METHOD
        }
      },
      deliveryMethod: {
        type: 'string',
        title: '交货方式',
        required: true,
        'x-decorator': FormItem,
        'x-component': DictSelect,
        'x-component-props': {
          code: AuctionCode.DELIVERY_METHOD
        }
      },
      [PRICING_METHOD_FIELD]: {
        type: 'object',
        title: '计价方式',
        required: true,
        'x-decorator': FormItem,
        'x-component': PricingMethodSelect,
        'x-component-props': {
          supportTradeType: '0'
        }
      },
      quantityFormGrid: {
        type: 'void',
        'x-component': FormGrid,
        'x-component-props': {
          wrap: false,
          columns: 2
        },
        properties: {
          quantity: {
            type: 'number',
            title: '数量',
            'x-decorator': FormItem,
            'x-component': InputNumber,
            'x-component-props': {
              min: 0,
              addonAfter: '{{ $values.quantityUnit }}'
            },
            'x-validator': quantityValidator
          },
          [SPLIT_COUNT_FIELD]: {
            type: 'number',
            'x-decorator': FormItem,
            'x-component': SplitCountInput,
            'x-validator': splitCountValidator
          }
        }
      },
      [START_PRICE_FIELD]: {
        type: 'object',
        title: '起拍价',
        'x-decorator': FormItem,
        'x-component': StartPriceInput,
        'x-validator': startPriceValidator
      },
      priceStepFormGrid: {
        type: 'void',
        'x-component': FormGrid,
        'x-component-props': {
          wrap: false,
          columns: 3
        },
        properties: {
          [`[minAddPrice, maxAddPrice]`]: {
            type: 'number',
            title: '加价幅度',
            'x-decorator': FormItem,
            'x-decorator-props': {
              gridSpan: 2
            },
            'x-component': InputNumberRange,
            'x-component-props': {
              min: 0
            },
            'x-validator': addPriceRangeValidator
          },
          maxDecimalDigits: {
            type: 'number',
            title: '最大小数位',
            'x-decorator': FormItem,
            'x-component': InputNumber,
            'x-component-props': {
              min: 0,
              max: 4,
              precision: 0
            }
          }
        }
      },
      [`{depositValue, depositMethod}`]: {
        type: 'object',
        title: '保证金',
        'x-decorator': FormItem,
        'x-component': DepositInput,
        'x-validator': depositValidator
      },
      [`{entranceFeeMethod,entranceFeeValue}`]: {
        type: 'number',
        title: '入场费',
        'x-decorator': FormItem,
        'x-component': EntranceFeeInput,
        'x-component-props': {
          addonAfter: '元'
        },
        'x-validator': entranceFeeValidator
      },
      [SERVICE_FEE_FIELD]: {
        type: 'object',
        title: '服务费',
        'x-decorator': FormItem,
        'x-component': ServiceFeeInput,
        'x-validator': serviceFeeValidator
      },
      serviceFeeAdvanceAmount: {
        type: 'number',
        title: '预收额',
        'x-decorator': FormItem,
        'x-decorator-props': {
          fullness: true
        },
        'x-component': InputNumber,
        'x-component-props': {
          min: 0,
          addonAfter: '元'
        }
      },
      dealPriceType: {
        type: 'number',
        title: '成交方式',
        enum: mapNumberEnumOptions(DealPriceTypeTextMap),
        'x-decorator': FormItem,
        'x-component': Select
      },
      dealCountGrid: {
        type: 'void',
        'x-component': FormGrid,
        'x-component-props': {
          wrap: false,
          columns: 2
        },
        properties: {
          dealCount: {
            type: 'number',
            title: '成交人数',
            'x-pattern':
              '{{$form.editable && $values.wholeSale?"editable":"disabled"}}',
            enum: Array.from({ length: MAX_DEAL_COUNT }, (_, i) => i + 1),
            'x-decorator': FormItem,
            'x-component': Select
          },
          splitMethod: {
            type: 'number',
            title: '成交数量方式',
            'x-pattern':
              '{{$form.editable && $values.wholeSale?"editable":"disabled"}}',
            enum: mapNumberEnumOptions(SplitMethodTextMap),
            'x-decorator': FormItem,
            'x-component': Select
          }
        }
      },
      splitValues: {
        type: 'array',
        'x-decorator': FormItem,
        'x-decorator-props': {
          gridSpan: 2
        },
        'x-component': SplitValuesInput,
        'x-component-props': {
          dealCount: '{{ $values.dealCount ?? 0 }}',
          maxDealCount: MAX_DEAL_COUNT
        },
        'x-validator': splitValuesValidator
      },
      [`{allowDelay,delayTotalCount}`]: {
        title: '计时方式',
        type: 'object',
        'x-decorator': FormItem,
        'x-component': TimeMethodInput,
        'x-validator': delayCountValidator
      },
      startTime: {
        type: 'string',
        title: '开始时间',
        'x-decorator': FormItem,
        'x-component': StartTimePicker,
        'x-component-props': {
          showTime: true,
          disabledDate: (currentDate: Dayjs) =>
            currentDate.isBefore(dayjs().subtract(1, 'd'))
        },
        'x-validator': startTimeValidator
      },
      endTime: {
        type: 'string',
        title: '结束时间',
        'x-decorator': FormItem,
        'x-component': EndTimePicker,
        'x-component-props': {
          showTime: true
        },
        'x-validator': endTimeValidator
      },
      [`{
        id: contractTemplateId,
        name: contractTemplateName
        }`]: {
        type: 'object',
        title: '合同模板',
        'x-decorator': FormItem,
        'x-component': ContractTemplateSelect,
        'x-component-props': {
          bizType: BizType.RANKING_AUCTION,
          memberId: '{{ $values.memberId }}'
        }
      },
      '[contractStartTime,contractEndTime]': {
        type: 'array',
        title: '合同有效期',
        'x-decorator': FormItem,
        'x-component': DateRangePicker
      },
      taxRate: {
        type: 'number',
        title: '税率',
        'x-decorator': FormItem,
        'x-component': DictSelect,
        'x-component-props': {
          code: 'TAX_RATE',
          showValue: false
        }
      },
      [LIMIT_IP_FIELD]: {
        type: 'boolean',
        default: true,
        title: (
          <Space>
            <span>限制IP</span>
            <Tooltip
              title={
                <>
                  <div>1. 开启后，每个 IP 地址仅允许一个客户出价。</div>
                  <div>
                    2. “存放地” 为“淮北矿业”及其下属地址时，必须限制IP。
                  </div>
                </>
              }
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </Space>
        ),
        'x-decorator': FormItem,
        'x-component': Switch
      }
    }
  }
}
